import { NetworkedComponent, networkManager } from '@wonderlandcloud/client';
import {Component, PhysXComponent, Property} from '@wonderlandengine/api';
import {ComponentProperty, property} from '@wonderlandengine/api/decorators.js';
import Logger from './utils/logger.js';
import { vec3, vec4, quat } from 'gl-matrix';
import { EventsClient } from './events-client.js';
import { Events } from './network-events.js';

const MODULE = 'Orchestrated';

/**
 * orchestrated
 */
export class Orchestrated extends NetworkedComponent {
    static TypeName = 'orchestrated';

    /* Properties that are configurable in the editor */
    @property.string()
    networkNametag: string = 'XXXXX_YYYYY_ZZZZZ';

    /* 4 member vector. First 3 are position array, x,y,z. Fourth is number of seconds to traverse to next position from here. */
    @property.array(Property.vector4())
    pathLocations!: vec4[];

    /* Quaternion rotation for each point referenced in pathLocations */
    @property.array(Property.vector4())
    pathRotations!: vec4[];
    
    /**
     * Reference to the PhysX component for kinematic control
     */
    private physx: PhysXComponent | null = null;
    
    /**
     * Whether this component has already been registered with EventsClient
     */
    private registeredWithEventsClient: boolean = false;

    /**
     * Stored event listener so we can remove it on deactivate
     */
    private orchestratedInfoResponseListener: ((e: any) => void) | null = null;

    /**
     * Current path segment index (traveling from pathLocations[currentSegment] to pathLocations[currentSegment + 1])
     */
    private currentSegment: number = 0;

    /**
     * Progress through current segment (0.0 to 1.0)
     */
    private segmentProgress: number = 0.0;

    /**
     * Whether this instance is the path owner (sends transforms)
     */
    private isPathOwner: boolean = false;

    /**
     * Tracked segment and progress when receiving updates (non-owner)
     * Used to seamlessly transition to owner mode
     */
    private trackedSegment: number = 0;
    private trackedProgress: number = 0.0;
    private hasTrackedPosition: boolean = false;

    /**
     * Historical transform data for velocity-based path tracking
     */
    private transformHistory: Array<{pos: vec3, rot: quat, time: number}> = [];
    private readonly MAX_HISTORY_SAMPLES = 10;
    private lastTrackTime: number = 0;

    /**
     * Temporary vectors for interpolation calculations
     */
    private tempPos: vec3 = vec3.create();
    private tempRot: quat = quat.create();

    override start() {        
        // Get reference to PhysX component if present
        this.physx = this.object.getComponent(PhysXComponent);
        if (this.physx) {
            this.physx.kinematic = true; // Start as kinematic until network initialized
        }

        if (this.networkId !== -1) {
            super.start();
        }
    }

    /**
     * Complete initialization after receiving networkId from server
     * Called by EventsClient when associated object info arrives
     */
    completeInitialization(transformOwner: boolean) {
        // Safety check: ensure object is still valid
        if (this.object.isDestroyed) {
            Logger.warn(`Cannot complete initialization for '${this.networkNametag}' - object was destroyed`, MODULE);
            return;
        }

Logger.debug("Attempting to complete init for object ID: " + this.networkId, MODULE);
        if (this.networkId !== -1) {
            super.start();
            
            // Mark that we've already registered to prevent double registration in onActivate()
            this.registeredWithEventsClient = true;
            
            // Store ownership status
            this.isPathOwner = transformOwner;
            
            // Register with networkManager

Logger.debug("Registering with ownership bool value: " + transformOwner, MODULE);
            networkManager.registerNetworkedComponent(this, transformOwner);
            
            // Initialize at first path location if owner
            if (this.isPathOwner && this.pathLocations && this.pathLocations.length > 0) {
                this.initializeAtStartPosition();
            }
        }
    }

    becomePathOwner(snapshotTransform?: number[]) {
        this.isPathOwner = true;
        (this as any).mode = 'send';
        networkManager.unregisterNetworkedComponent(this, false);
        networkManager.registerNetworkedComponent(this, true);
        
        // Calculate current position on path instead of starting over
        if (this.pathLocations && this.pathLocations.length > 0) {
            const snapshot = snapshotTransform ? this.decodeSnapshotTransform(snapshotTransform) : null;
            if (snapshot) {
                this.object.setRotationWorld(snapshot.rot);
                this.object.setPositionWorld(snapshot.pos);
                this.simplePathMatch(snapshot.pos, snapshot.rot);
            }
            this.calculateCurrentPathPosition();
        }
    }

    override onActivate() {
        Logger.debug("Orchestrated onActivate called for nametag: " + this.networkNametag, MODULE);
        // Set mode to receive initially
        (this as any).mode = 'receive';
        
        // Register with networkManager as read component
        //networkManager.registerNetworkedComponent(this, false);
        
        // Request orchestrated object info from server
        if (EventsClient.instance) {
            // Get initial transform from object's current position
            const pos = this.object.getPositionWorld(vec3.create());
            const rot = this.object.getRotationWorld(quat.create());
            
            // Encode as dual quaternion
            const transform = [
                rot[0], rot[1], rot[2], rot[3],  // rotation (qr)
                pos[0] * 0.5, pos[1] * 0.5, pos[2] * 0.5, 0  // translation encoded in dual part (qd)
            ];
            
            EventsClient.instance.requestOrchestratedInfo(this.networkNametag, transform);

            // Register listener for server response
            Logger.info(`[ORCH] Registering orchestratedInfoResponseListener for nametag=${this.networkNametag}`, MODULE);
            this.orchestratedInfoResponseListener = (e: any) => {
                // Safety check: ensure object is still valid before processing any event
                if (this.object.isDestroyed) {
                    return;
                }

                if (e.type === Events.ORCHESTRATED_INFO_RESPONSE && e.data.nametag === this.networkNametag) {
                    const orchOwner = e.data.orchOwner;
                    const isOwner = orchOwner === this.localPlayerId;
Logger.debug("Orchestrated isOwner: " + isOwner, MODULE);

Logger.debug("Orchestrated setting this networkID to: " + e.data.networkId, MODULE);
                    // Set networkId from response
                    let oldId = this.networkId;
                    this.networkId = e.data.networkId;

                    // If we're the owner, switch to send mode
                    if (isOwner) {
                        (this as any).mode = 'send';
                        // Re-register as write component
Logger.debug("Orchestrated removing tracked object by ID: " + oldId, MODULE);
                        networkManager.removeObject(oldId);
                        //This will be re-registered with the networkManager in completeInitialization() called below, which is triggered by EventsClient after this response is processed
                    }
                    
                    // Complete initialization
                    this.completeInitialization(isOwner);
                } else if (e.type === Events.ORCHESTRATED_SNAPSHOT && !this.object.isDestroyed) {
                    const o = e.data?.orchestratedObjects;
                    Logger.info(`[ORCH SNAPSHOT] nametag=${this.networkNametag}, this.networkId=${this.networkId}, isPathOwner=${this.isPathOwner}, localPlayerId=${this.localPlayerId}, objects=${JSON.stringify((o||[]).map((x:any)=>({networkId:x?.networkId,orchOwner:x?.orchOwner})))}`, MODULE);
                    if (Array.isArray(o)) {
                        for (const orchestratedObj of o) {
                            if (orchestratedObj && typeof orchestratedObj.networkId === 'number') {
                                const networkIdMatch = orchestratedObj.networkId === this.networkId;
                                const ownerMatch = orchestratedObj.orchOwner === this.localPlayerId;
                                Logger.info(`[ORCH SNAPSHOT] orchObj.networkId=${orchestratedObj.networkId} vs this.networkId=${this.networkId} => match=${networkIdMatch}; orchObj.orchOwner=${orchestratedObj.orchOwner} (type=${typeof orchestratedObj.orchOwner}) vs localPlayerId=${this.localPlayerId} (type=${typeof this.localPlayerId}) => match=${ownerMatch}; isPathOwner=${this.isPathOwner}`, MODULE);
                                if (networkIdMatch && !this.isPathOwner) {
                                    if (ownerMatch) {
                                        Logger.info(`[ORCH SNAPSHOT] Becoming path owner for ${this.networkNametag}`, MODULE);
                                        this.becomePathOwner(orchestratedObj.transform);
                                    }
                                }
                            }
                        }
                    }
                }
            };
            networkManager.onEvent.add(this.orchestratedInfoResponseListener);
        }
    }

    override onDeactivate() {
        // Remove event listener
        if (this.orchestratedInfoResponseListener) {
            networkManager.onEvent.remove(this.orchestratedInfoResponseListener);
            this.orchestratedInfoResponseListener = null;
        }

        // Unregister from EventsClient
        if (EventsClient.instance) {
            EventsClient.instance.unregisterNetworkedOrchestrated(this.networkId);
        }
        
        super.onDeactivate();
    }

    update(dt: number) {
        // If we're not the owner, track our current position on the path
        if (!this.isPathOwner && this.pathLocations && this.pathLocations.length >= 2) {
            this.trackCurrentPathPosition();
            return;
        }
        
        // Only update path if we're the owner
        if (!this.isPathOwner || !this.pathLocations || this.pathLocations.length < 2) {
            return;
        }

        // Get current segment data
        const fromLoc = this.pathLocations[this.currentSegment];
        const nextSegmentIndex = (this.currentSegment + 1) % this.pathLocations.length;
        const toLoc = this.pathLocations[nextSegmentIndex];
        
        // Extract travel time from 4th component of current location (in seconds)
        const travelTime = fromLoc[3] || 1.0; // Default to 1 second if not specified
        
        // Calculate progress increment for this frame
        const progressDelta = dt / travelTime;
        this.segmentProgress += progressDelta;
        
        // Check if we've completed this segment
        if (this.segmentProgress >= 1.0) {
            this.segmentProgress = 0.0;
            this.currentSegment = nextSegmentIndex;
            return; // Process next segment on next frame
        }
        
        // Interpolate position (linear interpolation)
        const fromPos = vec3.fromValues(fromLoc[0], fromLoc[1], fromLoc[2]);
        const toPos = vec3.fromValues(toLoc[0], toLoc[1], toLoc[2]);
        vec3.lerp(this.tempPos, fromPos, toPos, this.segmentProgress);
        this.object.setPositionWorld(this.tempPos);
        
        // Interpolate rotation if rotations are configured
        if (this.pathRotations && this.pathRotations.length > 0) {
            const fromRotIndex = this.currentSegment % this.pathRotations.length;
            const toRotIndex = nextSegmentIndex % this.pathRotations.length;
            
            const fromRot = this.pathRotations[fromRotIndex];
            const toRot = this.pathRotations[toRotIndex];
            
            // Convert vec4 to quat and perform spherical interpolation
            const fromQuat = quat.fromValues(fromRot[0], fromRot[1], fromRot[2], fromRot[3]);
            const toQuat = quat.fromValues(toRot[0], toRot[1], toRot[2], toRot[3]);
            quat.slerp(this.tempRot, fromQuat, toQuat, this.segmentProgress);
            this.object.setRotationLocal(this.tempRot);
        }
    }

    /**
     * Track current position along the path when receiving updates (non-owner)
     * Continuously updates trackedSegment and trackedProgress using velocity analysis
     */
    private trackCurrentPathPosition() {
        if (!this.pathLocations || this.pathLocations.length < 2) return;
        
        const currentTime = performance.now() / 1000; // Convert to seconds
        const currentPos = this.object.getPositionWorld(vec3.create());
        const currentRot = this.object.getRotationLocal(quat.create());
        
        // Add current transform to history
        this.transformHistory.push({
            pos: vec3.clone(currentPos),
            rot: quat.clone(currentRot),
            time: currentTime
        });
        
        // Limit history size
        if (this.transformHistory.length > this.MAX_HISTORY_SAMPLES) {
            this.transformHistory.shift();
        }
        
        // Need at least 2 samples to calculate velocity
        if (this.transformHistory.length < 2) {
            // Fallback to simple position matching for first frame
            this.simplePathMatch(currentPos, currentRot);
            return;
        }
        
        // Calculate observed velocity from recent history
        const recentCount = Math.min(5, this.transformHistory.length);
        const oldest = this.transformHistory[this.transformHistory.length - recentCount];
        const newest = this.transformHistory[this.transformHistory.length - 1];
        const timeDelta = newest.time - oldest.time;
        
        if (timeDelta < 0.001) {
            // Not enough time has passed
            return;
        }
        
        const observedVelocity = vec3.subtract(vec3.create(), newest.pos, oldest.pos);
        vec3.scale(observedVelocity, observedVelocity, 1.0 / timeDelta);
        const observedSpeed = vec3.length(observedVelocity);
        
        // Calculate observed angular velocity
        const rotDelta = quat.create();
        quat.multiply(rotDelta, newest.rot, quat.invert(quat.create(), oldest.rot));
        const rotAngle = 2 * Math.acos(Math.min(1.0, Math.abs(rotDelta[3])));
        const observedAngularSpeed = rotAngle / timeDelta;
        
        // Find best matching segment based on velocity and position
        let bestSegment = 0;
        let bestProgress = 0.0;
        let bestScore = Number.MAX_VALUE;
        
        const isRotationOnlyPath = this.isRotationOnlyPath();
        
        for (let i = 0; i < this.pathLocations.length; i++) {
            const fromLoc = this.pathLocations[i];
            const nextIndex = (i + 1) % this.pathLocations.length;
            const toLoc = this.pathLocations[nextIndex];
            
            const fromPos = vec3.fromValues(fromLoc[0], fromLoc[1], fromLoc[2]);
            const toPos = vec3.fromValues(toLoc[0], toLoc[1], toLoc[2]);
            
            const travelTime = fromLoc[3] || 1.0;
            const segmentVec = vec3.subtract(vec3.create(), toPos, fromPos);
            const segmentLength = vec3.length(segmentVec);
            
            if (isRotationOnlyPath && this.pathRotations && this.pathRotations.length > i) {
                // For rotation-only paths, match based on rotation similarity AND direction of change
                const fromRotIndex = i % this.pathRotations.length;
                const toRotIndex = nextIndex % this.pathRotations.length;
                
                const fromRot = this.pathRotations[fromRotIndex];
                const toRot = this.pathRotations[toRotIndex];
                
                const fromQuat = quat.fromValues(fromRot[0], fromRot[1], fromRot[2], fromRot[3]);
                const toQuat = quat.fromValues(toRot[0], toRot[1], toRot[2], toRot[3]);
                
                // Calculate expected rotation direction on this segment
                const expectedRotDelta = quat.create();
                quat.multiply(expectedRotDelta, toQuat, quat.invert(quat.create(), fromQuat));
                
                // Sample multiple points along the segment to find best rotation match
                let bestT = 0.0;
                let bestRotError = Number.MAX_VALUE;
                
                for (let tSample = 0.0; tSample <= 1.0; tSample += 0.1) {
                    const sampledRot = quat.create();
                    quat.slerp(sampledRot, fromQuat, toQuat, tSample);
                    
                    const dot = Math.abs(quat.dot(currentRot, sampledRot));
                    const rotError = 1.0 - Math.min(1.0, dot);
                    
                    if (rotError < bestRotError) {
                        bestRotError = rotError;
                        bestT = tSample;
                    }
                }
                
                // Add penalty if rotation direction doesn't match observed direction
                let directionPenalty = 0.0;
                let deltaDot = 0.0;
                if (this.transformHistory.length >= 2) {
                    // Get observed rotation change direction from history
                    const oldRot = this.transformHistory[this.transformHistory.length - 2].rot;
                    const observedRotDelta = quat.create();
                    quat.multiply(observedRotDelta, currentRot, quat.invert(quat.create(), oldRot));
                    
                    // Compare the rotation delta quaternions
                    // If they represent rotations in opposite directions, the dot product will be negative
                    deltaDot = quat.dot(observedRotDelta, expectedRotDelta);
                    
                    // If rotation directions don't match (negative dot), add a large penalty
                    if (deltaDot < 0) {
                        directionPenalty = 10.0; // Large penalty for wrong direction
                    } else {
                        // Small penalty based on how well the directions align
                        directionPenalty = (1.0 - Math.abs(deltaDot)) * 0.5;
                    }
                }
                
                const totalError = bestRotError + directionPenalty;
                
                if (totalError < bestScore) {
                    bestScore = totalError;
                    bestSegment = i;
                    bestProgress = bestT;
                }
            } else {
                // Standard velocity-based matching for paths with positional movement
                const expectedSpeed = segmentLength / travelTime;
                
                const expectedVelocity = vec3.create();
                if (segmentLength > 0.0001) {
                    vec3.normalize(expectedVelocity, segmentVec);
                    vec3.scale(expectedVelocity, expectedVelocity, expectedSpeed);
                }
                
                // Find closest point on segment
                const toCurrentVec = vec3.subtract(vec3.create(), currentPos, fromPos);
                const t = segmentLength > 0.0001 
                    ? Math.max(0, Math.min(1, vec3.dot(toCurrentVec, segmentVec) / (segmentLength * segmentLength)))
                    : 0;
                
                const pointOnSegment = vec3.scaleAndAdd(vec3.create(), fromPos, segmentVec, t);
                const posError = vec3.distance(currentPos, pointOnSegment);
                
                // Compare velocities - use dot product to detect opposite directions
                let velocityError = vec3.distance(observedVelocity, expectedVelocity);
                
                // Check velocity direction alignment
                if (observedSpeed > 0.01 && expectedSpeed > 0.01) {
                    const observedDir = vec3.create();
                    const expectedDir = vec3.create();
                    vec3.normalize(observedDir, observedVelocity);
                    vec3.normalize(expectedDir, expectedVelocity);
                    const directionDot = vec3.dot(observedDir, expectedDir);
                    
                    // If directions are opposite (dot < 0), add a very large penalty
                    if (directionDot < 0) {
                        velocityError += 100.0; // Massive penalty for wrong direction
                    }
                }
                
                // Calculate expected angular velocity if rotations exist
                let angularError = 0.0;
                if (this.pathRotations && this.pathRotations.length > i) {
                    const fromRotIndex = i % this.pathRotations.length;
                    const toRotIndex = nextIndex % this.pathRotations.length;
                    
                    const fromRot = this.pathRotations[fromRotIndex];
                    const toRot = this.pathRotations[toRotIndex];
                    
                    const fromQuat = quat.fromValues(fromRot[0], fromRot[1], fromRot[2], fromRot[3]);
                    const toQuat = quat.fromValues(toRot[0], toRot[1], toRot[2], toRot[3]);
                    
                    // Calculate rotation difference over segment
                    const rotDiff = quat.create();
                    quat.multiply(rotDiff, toQuat, quat.invert(quat.create(), fromQuat));
                    const expectedRotAngle = 2 * Math.acos(Math.min(1.0, Math.abs(rotDiff[3])));
                    const expectedAngularSpeed = expectedRotAngle / travelTime;
                    
                    angularError = Math.abs(observedAngularSpeed - expectedAngularSpeed);
                }
                
                // Combined score: position error + velocity error + angular error
                // Weight velocity heavily since it's the key differentiator
                const score = posError + (velocityError * 2.0) + (angularError * 0.5);
                
                if (score < bestScore) {
                    bestScore = score;
                    bestSegment = i;
                    bestProgress = t;
                }
            }
        }
        
        this.trackedSegment = bestSegment;
        this.trackedProgress = bestProgress;
        this.hasTrackedPosition = true;
    }
    
    /**
     * Simple position/rotation matching fallback when insufficient history
     */
    private simplePathMatch(currentPos: vec3, currentRot: quat) {
        if (this.isRotationOnlyPath() && this.pathRotations && this.pathRotations.length > 0) {
            const match = this.matchRotationOnlyPath(currentRot);
            this.trackedSegment = match.segment;
            this.trackedProgress = match.progress;
            this.hasTrackedPosition = true;
            return;
        }

        let closestSegment = 0;
        let closestProgress = 0.0;
        let minDistance = Number.MAX_VALUE;
        
        for (let i = 0; i < this.pathLocations!.length; i++) {
            const fromLoc = this.pathLocations![i];
            const nextIndex = (i + 1) % this.pathLocations!.length;
            const toLoc = this.pathLocations![nextIndex];
            
            const fromPos = vec3.fromValues(fromLoc[0], fromLoc[1], fromLoc[2]);
            const toPos = vec3.fromValues(toLoc[0], toLoc[1], toLoc[2]);
            
            const segmentVec = vec3.subtract(vec3.create(), toPos, fromPos);
            const toCurrentVec = vec3.subtract(vec3.create(), currentPos, fromPos);
            
            const segmentLength = vec3.length(segmentVec);
            if (segmentLength > 0.0001) {
                const t = Math.max(0, Math.min(1, vec3.dot(toCurrentVec, segmentVec) / (segmentLength * segmentLength)));
                const pointOnSegment = vec3.scaleAndAdd(vec3.create(), fromPos, segmentVec, t);
                const distance = vec3.distance(currentPos, pointOnSegment);
                
                if (distance < minDistance) {
                    minDistance = distance;
                    closestSegment = i;
                    closestProgress = t;
                }
            }
        }
        
        this.trackedSegment = closestSegment;
        this.trackedProgress = closestProgress;
        this.hasTrackedPosition = true;
    }

    private decodeSnapshotTransform(transform: number[]): { pos: vec3; rot: quat } | null {
        if (!Array.isArray(transform) || transform.length < 8) return null;

        const rot = quat.fromValues(transform[0], transform[1], transform[2], transform[3]);
        const qd = {
            x: transform[4],
            y: transform[5],
            z: transform[6],
            w: transform[7]
        };
        const qrConj = { x: -rot[0], y: -rot[1], z: -rot[2], w: rot[3] };

        const temp = {
            w: qd.w * qrConj.w - qd.x * qrConj.x - qd.y * qrConj.y - qd.z * qrConj.z,
            x: qd.w * qrConj.x + qd.x * qrConj.w + qd.y * qrConj.z - qd.z * qrConj.y,
            y: qd.w * qrConj.y - qd.x * qrConj.z + qd.y * qrConj.w + qd.z * qrConj.x,
            z: qd.w * qrConj.z + qd.x * qrConj.y - qd.y * qrConj.x + qd.z * qrConj.w
        };

        const pos = vec3.fromValues(temp.x * 2, temp.y * 2, temp.z * 2);
        return { pos, rot };
    }

    private matchRotationOnlyPath(currentRot: quat): { segment: number; progress: number } {
        let bestSegment = 0;
        let bestProgress = 0.0;
        let bestRotError = Number.MAX_VALUE;

        for (let i = 0; i < this.pathLocations.length; i++) {
            const nextIndex = (i + 1) % this.pathLocations.length;
            const fromRotIndex = i % this.pathRotations.length;
            const toRotIndex = nextIndex % this.pathRotations.length;

            const fromRot = this.pathRotations[fromRotIndex];
            const toRot = this.pathRotations[toRotIndex];

            const fromQuat = quat.fromValues(fromRot[0], fromRot[1], fromRot[2], fromRot[3]);
            const toQuat = quat.fromValues(toRot[0], toRot[1], toRot[2], toRot[3]);

            for (let tSample = 0.0; tSample <= 1.0; tSample += 0.05) {
                const sampledRot = quat.create();
                quat.slerp(sampledRot, fromQuat, toQuat, tSample);

                const dot = Math.abs(quat.dot(currentRot, sampledRot));
                const rotError = 1.0 - Math.min(1.0, dot);

                if (rotError < bestRotError) {
                    bestRotError = rotError;
                    bestSegment = i;
                    bestProgress = tSample;
                }
            }
        }

        return { segment: bestSegment, progress: bestProgress };
    }

    private isRotationOnlyPath(): boolean {
        let maxSegmentLength = 0;
        for (let i = 0; i < this.pathLocations.length; i++) {
            const fromLoc = this.pathLocations[i];
            const nextIndex = (i + 1) % this.pathLocations.length;
            const toLoc = this.pathLocations[nextIndex];
            const segLen = vec3.distance(
                vec3.fromValues(fromLoc[0], fromLoc[1], fromLoc[2]),
                vec3.fromValues(toLoc[0], toLoc[1], toLoc[2])
            );
            maxSegmentLength = Math.max(maxSegmentLength, segLen);
        }
        return maxSegmentLength < 0.01;
    }

    /**
     * Calculate current position along the path based on tracked data or current transform
     * Sets currentSegment and segmentProgress to resume path movement
     */
    private calculateCurrentPathPosition() {
        if (!this.pathLocations || this.pathLocations.length < 2) return;
        
        // If we've been tracking position as a receiver, use that data
        if (this.hasTrackedPosition) {
            this.currentSegment = this.trackedSegment;
            this.segmentProgress = this.trackedProgress;
            return;
        }
        
        // Fallback: calculate from current position (less reliable)
        const currentPos = this.object.getPositionWorld(vec3.create());
        const currentRot = this.object.getRotationLocal(quat.create());

        if (this.isRotationOnlyPath() && this.pathRotations && this.pathRotations.length > 0) {
            const match = this.matchRotationOnlyPath(currentRot);
            this.currentSegment = match.segment;
            this.segmentProgress = match.progress;
            return;
        }
        
        let closestSegment = 0;
        let closestProgress = 0.0;
        let minDistance = Number.MAX_VALUE;
        
        // Check each segment to find closest point
        for (let i = 0; i < this.pathLocations.length; i++) {
            const fromLoc = this.pathLocations[i];
            const nextIndex = (i + 1) % this.pathLocations.length;
            const toLoc = this.pathLocations[nextIndex];
            
            const fromPos = vec3.fromValues(fromLoc[0], fromLoc[1], fromLoc[2]);
            const toPos = vec3.fromValues(toLoc[0], toLoc[1], toLoc[2]);
            
            // Find closest point on this segment
            const segmentVec = vec3.subtract(vec3.create(), toPos, fromPos);
            const toCurrentVec = vec3.subtract(vec3.create(), currentPos, fromPos);
            
            const segmentLength = vec3.length(segmentVec);
            if (segmentLength > 0.0001) {
                const t = Math.max(0, Math.min(1, vec3.dot(toCurrentVec, segmentVec) / (segmentLength * segmentLength)));
                
                // Calculate point on segment
                const pointOnSegment = vec3.scaleAndAdd(vec3.create(), fromPos, segmentVec, t);
                const posDistance = vec3.distance(currentPos, pointOnSegment);
                
                // Factor in rotation distance if rotations are configured
                let rotDistance = 0.0;
                if (this.pathRotations && this.pathRotations.length > i) {
                    const fromRotIndex = i;
                    const toRotIndex = nextIndex < this.pathRotations.length ? nextIndex : 0;
                    
                    const fromRot = this.pathRotations[fromRotIndex];
                    const toRot = this.pathRotations[toRotIndex];
                    
                    const fromQuat = quat.fromValues(fromRot[0], fromRot[1], fromRot[2], fromRot[3]);
                    const toQuat = quat.fromValues(toRot[0], toRot[1], toRot[2], toRot[3]);
                    
                    // Interpolate rotation at parameter t
                    const interpolatedRot = quat.create();
                    quat.slerp(interpolatedRot, fromQuat, toQuat, t);
                    
                    // Calculate angular distance (1 - dot product of quaternions)
                    const dot = Math.abs(quat.dot(currentRot, interpolatedRot));
                    rotDistance = 1.0 - Math.min(1.0, dot);
                }
                
                // Combine position and rotation distances (weight rotation less than position)
                const combinedDistance = posDistance + (rotDistance * 0.5);
                
                if (combinedDistance < minDistance) {
                    minDistance = combinedDistance;
                    closestSegment = i;
                    closestProgress = t;
                }
            }
        }
        
        this.currentSegment = closestSegment;
        this.segmentProgress = closestProgress;
    }

    /**
     * Initialize object at the first path location
     */
    private initializeAtStartPosition() {
        // Safety check: ensure object is still valid
        if (this.object.isDestroyed) {
            Logger.warn(`Cannot initialize start position for '${this.networkNametag}' - object was destroyed`, MODULE);
            return;
        }

        if (!this.pathLocations || this.pathLocations.length === 0) return;
        
        const startLoc = this.pathLocations[0];
        const startPos = vec3.fromValues(startLoc[0], startLoc[1], startLoc[2]);
        this.object.setPositionWorld(startPos);
        
        if (this.pathRotations && this.pathRotations.length > 0) {
            const startRot = this.pathRotations[0];
            const startQuat = quat.fromValues(startRot[0], startRot[1], startRot[2], startRot[3]);
            this.object.setRotationLocal(startQuat);
        }
        
        this.currentSegment = 0;
        this.segmentProgress = 0.0;
    }

    /**
     * Local player's server-assigned user ID (set exclusively by SET_ID, never a networkObjectId).
     * More reliable than playerUserId which is temporarily set to joinEvent[0] on connect.
     */
    private get localPlayerId(): number {
        return EventsClient.instance?.serverAssignedUserId ?? -1;
    }
}
